import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { AlertProvider } from './context/AlertContext';

import FacebookPixel from './components/FacebookPixel';
import { HelmetProvider } from 'react-helmet-async';
import NotificationManager from './components/NotificationManager';

// Structured Data Markup
const structuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Gaplify",
  "description": "AI-powered career development platform bridging the gap between ambition and achievement",
  "url": "https://www.gaplify.com",
  "logo": "https://www.gaplify.com/logo.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91 8408868107",
    "contactType": "customer service"
  },
  "sameAs": [
    "https://www.linkedin.com/company/gaplify",
    "https://twitter.com/gaplify",
    "https://www.instagram.com/gaplify/",
    "https://www.facebook.com/gaplify"
  ]
};




import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import Signup from './components/Signup';
import Home from './components/Home';
import About from './components/About';
import Contact from './components/Contact';
import SkillsAnalysis from './components/SkillsAnalysis';

import TrendingSkills from './components/TrendingSkills';
import History from './components/History';
import ResourceHub from './pages/ResourceHub';
import ResourceDetails from './pages/ResourceDetails';
import Cart from './pages/Cart';
import Blog from './components/Blog';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Mentors from './components/Mentors';
import Roadmap from './components/Roadmap';
import Profile from './pages/Profile';
import initAnimations from './utils/animations';
import DailyQuiz from './components/DailyQuiz';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsConditions from './pages/legal/TermsConditions';
import FAQ from './pages/legal/FAQ';
import CookiePolicy from './pages/legal/CookiePolicy';
import Community from './pages/Community';
import Feedback from './pages/Feedback';
import AdminDashboard from './pages/AdminDashboard';
import BecomeMentor from './pages/BecomeMentor';
import SkillDetails from './pages/SkillDetails';
import MentorDashboard from './pages/MentorDashboard';
import OpenSourceProjects from './pages/OpenSourceProjects';
import ProjectDetails from './pages/ProjectDetails';



const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  useEffect(() => {
    // Initialize animations when the app loads
    initAnimations();
    
    // Add structured data to the document
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, []);

  return (
    <HelmetProvider>
      <FacebookPixel />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <AlertProvider>
        <AuthProvider>
          <NotificationManager />
          <CartProvider>
            <Router>
              <div className="flex flex-col min-h-screen bg-gray-50">
                <ScrollToTop />
                <Navbar />
                <main className="flex-1 relative pt-10 lg:pt-14">
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route
                      path="/"
                      element={
                        <Home />
                      }
                    />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route
                      path="/skills-analysis"
                      element={
                        <PrivateRoute>
                          <SkillsAnalysis />
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/trending-skills"
                      element={
                        <PrivateRoute>
                          <TrendingSkills />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/trending-skills/:domain/:skill"
                      element={
                        <PrivateRoute>
                          <SkillDetails />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/resource-hub"
                      element={
                        <PrivateRoute>
                          <ResourceHub />
                        </PrivateRoute>
                      }
                    />
                     
                    <Route
                      path="/resource/:id"
                      element={
                        <PrivateRoute>
                          <ResourceDetails />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/blog"
                      element={
                        <PrivateRoute>
                          <Blog />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/blog/:slug"
                      element={
                        <PrivateRoute>
                          <Blog />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/cart"
                      element={
                        <PrivateRoute>
                          <Cart />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/mentors"
                      element={
                        <PrivateRoute>
                          <Mentors />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/roadmap"
                      element={
                        <PrivateRoute>
                          <Roadmap />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/history"
                      element={
                        <PrivateRoute>
                          <History />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <PrivateRoute>
                          <Profile />
                        </PrivateRoute>
                      }
                    />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/feedback" element={<Feedback />} />
                    <Route path="/terms-and-conditions" element={<TermsConditions />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/community" element={<PrivateRoute><Community /></PrivateRoute>} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
                    <Route path="/become-mentor" element={<PrivateRoute><BecomeMentor /></PrivateRoute>} />
                    <Route path="/mentor/dashboard" element={<PrivateRoute><MentorDashboard /></PrivateRoute>} />
                    <Route path="/open-source" element={<PrivateRoute><OpenSourceProjects /></PrivateRoute>} />
                    <Route path="/open-source/:id" element={<PrivateRoute><ProjectDetails /></PrivateRoute>} />
                    <Route path="/daily-quiz" element={<PrivateRoute><DailyQuiz /></PrivateRoute>} />
                  </Routes>
                </main>
                <Footer />
              </div>
            </Router>
          </CartProvider>
        </AuthProvider>
      </AlertProvider>
    </HelmetProvider>
  );
}

export default App;
